import dayjs from "dayjs";
import { useSellarDistributionContactList } from "../../../hooks/api/v2/sellarDistributionContact/useSellarDistributionContactList";
import { useUnifiedCheckoutBasketList } from "../../../hooks/api/v2/unified-checkout-basket/useUnifiedCheckoutBaskets";
import { useUnifiedCheckout } from "../UnifiedCheckoutProvider.shared";
import type { UnifiedCheckoutSuccessOrder } from "./useUnifiedCheckoutSuccessPageData";

export type UnifiedCheckoutDeliveryGroup = {
  date: string;
  suppliers: string[];
};

export const useUnifiedCheckoutSuccessDetails = ({
  deliveryDates,
  orders,
}: {
  deliveryDates: string[];
  orders: UnifiedCheckoutSuccessOrder[];
}) => {
  const { sdBasket } = useUnifiedCheckout();
  const basketListQuery = useUnifiedCheckoutBasketList();
  const contactsQuery = useSellarDistributionContactList({ limit: 100 });
  const remainingBaskets =
    basketListQuery.data?.filter(
      (basket) =>
        basket.uuid !== sdBasket.uuid &&
        basket.status === "active" &&
        basket.DraftOrders.some((order) => order.Items.length > 0),
    ) ?? [];
  const contact = contactsQuery.data?.pages
    .flat()
    .find((entry) => entry.id === sdBasket.contactId);
  const deliveryGroups = deliveryDates.map((date) => ({
    date,
    suppliers: orders
      .filter(({ order }) =>
        sdBasket.deliveryPreference === "split"
          ? dayjs(order.deliveryPlannedAt).format("YYYY-MM-DD") === date
          : true,
      )
      .map(({ order }) => order.Supplier.Business.name),
  }));

  return {
    deliveryGroups,
    deliveryPreference: sdBasket.deliveryPreference ?? "consolidated",
    remainingBasketUuid: remainingBaskets[0]?.uuid,
    remainingBasketCount: remainingBaskets.length,
    remainingBasketItemCount: remainingBaskets.reduce(
      (basketCount, basket) =>
        basketCount +
        basket.DraftOrders.reduce(
          (draftCount, order) =>
            draftCount +
            order.Items.reduce(
              (itemCount, item) => itemCount + item.quantity,
              0,
            ),
          0,
        ),
      0,
    ),
    contactName: contact?.name ?? "Delivery contact",
    contactPhone:
      contact?.phoneNumber ?? sdBasket.phoneNumber ?? "Not provided",
    timePreference:
      sdBasket.timeSlotPreference === "AM"
        ? "AM"
        : sdBasket.timeSlotPreference === "PM"
          ? "Early PM"
          : "Flexible",
  };
};
